import { Helmet } from "react-helmet-async";
import { useHostname } from "@/lib/ssr";
import { OG_IMAGES, OG_LOCALE, siteOrigin } from "@/lib/seo/site";
import { HEADINGS_SORA, SORA_FONTS_URL, isSoraEnabled } from "@/lib/siteFont";

/**
 * Site-wide default <head> tags, rendered via Helmet so they are emitted during
 * SSR and can be overridden per-route: public marketing pages get their full head
 * from RouteMeta (src/lib/seo/pages.ts), the gallery viewer from GalleryMeta. Any
 * other page (sign-in, admin, uploader) falls back to these, so a shared link
 * still unfurls with the Photohawk card instead of nothing.
 */
const DEFAULT_TITLE = "Photohawk | Share and sell event photos, found by face or bib";
const DEFAULT_DESCRIPTION =
  "Get your event photos online fast. Share them for free or sell them, and let everyone find their photos by face or bib number, with branded galleries and secure checkout.";

const DefaultMeta = () => {
  const host = useHostname();
  const origin = siteOrigin(host);
  // The Sora headings (src/lib/siteFont.ts): where they're on, mark <html> (the CSS's key) and link the Sora
  // stylesheet, server-rendered so the first paint already uses it. Where they're off (prod until launch) neither is
  // rendered. Kept inside this Helmet rather than a component of its own: a new component in AppShell would shift
  // React's useId values and change prod's HTML.
  const sora = isSoraEnabled(host);
  const image = `${origin}${OG_IMAGES.default.path}`;
  return (
    <Helmet>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />

      {/* Warm up the Google Fonts origins so brand/gallery fonts start resolving
          before the app injects their stylesheet <link> — avoids a font flash. */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <meta name="author" content="Photohawk" />
      {sora && <html data-ph-headings={HEADINGS_SORA} />}
      {sora && <link rel="stylesheet" href={SORA_FONTS_URL} />}

      {/* Open Graph */}
      <meta property="og:site_name" content="Photohawk" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:locale" content={OG_LOCALE.en} />
      {/* No og:image:width/height here: a route that swaps in its own image (GalleryMeta) can't
          remove them, and wrong dimensions distort the card. RouteMeta adds them for its images. */}
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={OG_IMAGES.default.alt} />

      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={OG_IMAGES.default.alt} />
    </Helmet>
  );
};

export default DefaultMeta;
